import { memo } from 'react';
import { ClockIcon, LightningBoltIcon } from '@heroicons/react/outline';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';
import { useAnalytics } from '@/contexts/Analytics';

interface SubscriptionPromptOverlayProps {
  /** Unix timestamp (ms) when the queue expires */
  queuedUntil: number;
  className?: string;
}

/**
 * Premium subscription prompt overlay shown to free users during image generation.
 * Blocks access to the image until queuedUntil timestamp passes.
 */
function SubscriptionPromptOverlay({
  queuedUntil,
  className,
}: SubscriptionPromptOverlayProps) {
  const analytics = useAnalytics();

  const handleUpgradeClick = () => {
    analytics.track('Queue Upgrade CTA Clicked', {
      queuedUntil,
    });
  };

  return (
    <div
      className={clsxm(
        'relative overflow-hidden rounded-lg',
        'bg-gradient-to-br from-slate-50 via-white to-slate-50',
        'dark:from-slate-900 dark:via-slate-800 dark:to-slate-900',
        'ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/50',
        className,
      )}
    >
      {/* Ambient background glow */}
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-500/5 via-transparent to-transparent rounded-full blur-3xl' />
        <div className='absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-500/5 via-transparent to-transparent rounded-full blur-3xl' />
      </div>

      {/* Content container */}
      <div className='relative flex flex-col items-center justify-center h-full px-6 py-8 text-center'>
        {/* Queue indicator */}
        <div className='flex items-center gap-2 mb-4'>
          <div className='relative'>
            <div className='w-2 h-2 rounded-full bg-amber-400 animate-pulse' />
            <div className='absolute inset-0 w-2 h-2 rounded-full bg-amber-400/50 animate-ping' />
          </div>
          <span className='text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400'>
            In Queue
          </span>
        </div>

        {/* Static wait time display */}
        <div className='mb-4'>
          <div className='flex items-center justify-center gap-2 mb-1'>
            <ClockIcon className='w-5 h-5 text-slate-400 dark:text-slate-500' />
            <span className='text-2xl font-semibold text-slate-800 dark:text-slate-200'>
              45 min
            </span>
          </div>
          <p className='text-sm text-slate-500 dark:text-slate-400'>
            Estimated wait time
          </p>
        </div>

        {/* Message */}
        <p className='text-sm text-slate-600 dark:text-slate-300 mb-6 max-w-[240px] leading-relaxed'>
          High demand for free users. Upgrade for instant generation.
        </p>

        {/* Upgrade CTA */}
        <Link
          href='/settings/billing'
          onClick={handleUpgradeClick}
          className={clsxm(
            'group relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full',
            'text-sm font-semibold text-white',
            'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900',
            'dark:from-white dark:via-slate-100 dark:to-white',
            'dark:text-slate-900',
            'hover:shadow-lg hover:shadow-slate-900/20 dark:hover:shadow-white/20',
            'transform hover:-translate-y-0.5 active:translate-y-0',
            'transition-all duration-200 ease-out',
          )}
        >
          <LightningBoltIcon className='w-4 h-4' />
          <span>Skip the Queue</span>
          <span className='ms-1 inline-block text-xs opacity-60 rtl:-scale-x-100'>
            →
          </span>
        </Link>
      </div>

      <style jsx>{`
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-ping {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default memo(SubscriptionPromptOverlay);
